import React, { useState, useEffect, useCallback, useRef } from 'react';
import reportingFinancierApi from '../services/reporting-financier';
import './ReportingBudget.css';

const MOIS_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const JOURS_SEMAINE = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

// Parse une date 'YYYY-MM-DD' en date LOCALE. `new Date('2026-06-21')` est
// interprétée en UTC minuit ; relue en heure locale (Martinique = UTC-4), elle
// recule au 20/06 → getDay()/getMonth()/getDate() décalés d'un jour et grille
// calendaire glissée d'une colonne. On construit la date avec les composants
// locaux pour éviter tout décalage de fuseau.
const parseLocalDate = (s) => {
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};

const fmtMoney = (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const fmtNumber = (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v);

// ─── Sous-onglet BUDGET ─────────────────────────────────────────────────────

function BudgetTab({ user, setHeaderAction }) {
  const [lignes, setLignes] = useState([]);
  const [dimensions, setDimensions] = useState({ annees: [], services: [], concessions: [], marques: [] });
  const [annee, setAnnee] = useState(null); // null = pas encore initialisé
  const [filterService, setFilterService] = useState('');
  const [filterConcession, setFilterConcession] = useState('');
  const [loading, setLoading] = useState(false);
  const [editCell, setEditCell] = useState(null); // { id, value }
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Charger les dimensions au montage pour déterminer l'année par défaut
  useEffect(() => {
    reportingFinancierApi.getBudgetDimensions()
      .then(res => {
        const dims = res.data || { annees: [], services: [], concessions: [], marques: [] };
        setDimensions(dims);
        // Sélectionner la première année disponible (la plus récente), sinon année courante
        if (dims.annees.length > 0 && annee === null) {
          setAnnee(dims.annees[0]);
        } else if (annee === null) {
          setAnnee(new Date().getFullYear());
        }
      })
      .catch(() => { if (annee === null) setAnnee(new Date().getFullYear()); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = useCallback(async () => {
    if (annee === null) return;
    setLoading(true);
    try {
      const params = { annee };
      if (filterService) params.service = filterService;
      if (filterConcession) params.concession = filterConcession;
      const [lignesRes, dimsRes] = await Promise.all([
        reportingFinancierApi.getBudgetLignes(params),
        reportingFinancierApi.getBudgetDimensions(),
      ]);
      setLignes(lignesRes.data.data || []);
      setDimensions(dimsRes.data || { annees: [], services: [], concessions: [], marques: [] });
    } catch (err) {
      console.error('Erreur chargement budget:', err);
    } finally {
      setLoading(false);
    }
  }, [annee, filterService, filterConcession]);

  useEffect(() => { loadData(); }, [loadData]);

  // Header actions
  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction({
      primary: { label: '📥 Importer Budget', onClick: () => fileInputRef.current?.click() },
      secondary: [
        {
          label: (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export
            </span>
          ),
          onClick: handleExport
        },
      ]
    });
    return () => setHeaderAction(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderAction, annee]);

  async function handleImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await reportingFinancierApi.importBudget(formData);
      alert(`✅ ${res.data.inserted} lignes importées`);
      loadData();
    } catch (err) {
      alert('❌ Erreur import: ' + (err.response?.data?.error || err.message));
    }
    e.target.value = '';
  }

  async function handleExport() {
    try {
      const res = await reportingFinancierApi.exportBudget(annee);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Budget_${annee}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('❌ Erreur export');
    }
  }

  async function handleSaveCell(id, value) {
    setSaving(true);
    try {
      await reportingFinancierApi.updateBudgetLigne(id, parseFloat(value) || 0);
      setLignes(prev => prev.map(l => l.id === id ? { ...l, montant: parseFloat(value) || 0 } : l));
    } catch (err) {
      alert('❌ Erreur sauvegarde');
    } finally {
      setSaving(false);
      setEditCell(null);
    }
  }

  // Grouper les lignes par service > concession > marque > nature > catégorie
  // puis pivoter les mois en colonnes
  const grouped = {};
  for (const l of lignes) {
    const key = `${l.service}|${l.concession}|${l.marque}|${l.nature}|${l.categorie}`;
    if (!grouped[key]) {
      grouped[key] = { service: l.service, concession: l.concession, marque: l.marque, nature: l.nature, categorie: l.categorie, mois: {} };
    }
    grouped[key].mois[l.mois] = { id: l.id, montant: parseFloat(l.montant) };
  }
  const rows = Object.values(grouped);

  // Regrouper par service pour affichage avec séparateurs
  const serviceGroups = {};
  for (const r of rows) {
    if (!serviceGroups[r.service]) serviceGroups[r.service] = [];
    serviceGroups[r.service].push(r);
  }

  const formatCell = (nature, val) => {
    if (val == null) return '—';
    if (nature === 'CA' || nature === 'MARGE' || nature === 'Marge') return fmtMoney(val);
    return fmtNumber(val);
  };

  return (
    <div className="rb-budget-tab">
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />

      <div className="page-toolbar">
        <select className="page-select" value={annee} onChange={e => setAnnee(parseInt(e.target.value))}>
          {(dimensions.annees.length > 0 ? dimensions.annees : [new Date().getFullYear()]).map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select className={`page-select ${filterService ? 'active-filter' : ''}`} value={filterService} onChange={e => setFilterService(e.target.value)}>
          <option value="">Tous services</option>
          {dimensions.services.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={`page-select ${filterConcession ? 'active-filter' : ''}`} value={filterConcession} onChange={e => setFilterConcession(e.target.value)}>
          <option value="">Toutes concessions</option>
          {dimensions.concessions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="rb-loading">Chargement...</div>
      ) : rows.length === 0 ? (
        <div className="rb-empty">
          <p>Aucune donnée budget pour {annee}.</p>
          <p>Importez un fichier Excel pour commencer.</p>
        </div>
      ) : (
        <div className="table-container rb-budget-table-wrap">
          <table className="rb-budget-table">
            <thead>
              <tr>
                <th className="rb-col-fixed">Service</th>
                <th className="rb-col-fixed">Concession</th>
                <th className="rb-col-fixed">Marque</th>
                <th className="rb-col-fixed">Nature</th>
                <th className="rb-col-fixed">Catégorie</th>
                {MOIS_LABELS.map((m, i) => <th key={i} className="rb-col-mois">{m}</th>)}
                <th className="rb-col-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(serviceGroups).map(([svc, svcRows]) => (
                <React.Fragment key={svc}>
                  <tr className="rb-service-header">
                    <td colSpan={5 + 13}>{svc}</td>
                  </tr>
                  {svcRows.map((r, idx) => {
                    const total = Object.values(r.mois).reduce((s, m) => s + m.montant, 0);
                    return (
                      <tr key={idx}>
                        <td className="rb-cell-svc">{r.service}</td>
                        <td>{r.concession}</td>
                        <td>{r.marque}</td>
                        <td>{r.nature}</td>
                        <td>{r.categorie}</td>
                        {Array.from({ length: 12 }, (_, mi) => {
                          const cell = r.mois[mi + 1];
                          const isEditing = editCell && cell && editCell.id === cell.id;
                          return (
                            <td key={mi} className={`rb-cell-val ${cell ? '' : 'rb-cell-empty'}`}>
                              {isEditing ? (
                                <input
                                  className="rb-edit-input"
                                  type="number"
                                  autoFocus
                                  defaultValue={editCell.value}
                                  disabled={saving}
                                  onBlur={e => handleSaveCell(cell.id, e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') handleSaveCell(cell.id, e.target.value);
                                    if (e.key === 'Escape') setEditCell(null);
                                  }}
                                />
                              ) : cell ? (
                                <span
                                  className="rb-editable"
                                  onClick={() => setEditCell({ id: cell.id, value: cell.montant })}
                                  title="Cliquer pour modifier"
                                >
                                  {formatCell(r.nature, cell.montant)}
                                </span>
                              ) : '—'}
                            </td>
                          );
                        })}
                        <td className="rb-cell-total">{formatCell(r.nature, total)}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Sous-onglet JOURS OUVRÉS ───────────────────────────────────────────────

function JoursOuvresTab({ user, setHeaderAction }) {
  const [data, setData] = useState([]);
  const [dimensions, setDimensions] = useState({ departements: [], annees: [] });
  const [departement, setDepartement] = useState('');
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({ mensuel: [], totaux: [] });
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { annee };
      if (departement) params.departement = departement;
      const [joursRes, dimsRes, countsRes] = await Promise.all([
        reportingFinancierApi.getJoursOuvres(params),
        reportingFinancierApi.getJoursOuvresDimensions(),
        reportingFinancierApi.getJoursOuvresCount(params),
      ]);
      setData(joursRes.data.data || []);
      setDimensions(dimsRes.data || { departements: [], annees: [] });
      setCounts(countsRes.data || { mensuel: [], totaux: [] });
    } catch (err) {
      console.error('Erreur chargement jours ouvrés:', err);
    } finally {
      setLoading(false);
    }
  }, [annee, departement]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction({
      primary: { label: '📥 Importer Jours Ouvrés', onClick: () => fileInputRef.current?.click() },
    });
    return () => setHeaderAction(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderAction]);

  async function handleImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await reportingFinancierApi.importJoursOuvres(formData);
      alert(`✅ ${res.data.inserted} jours importés`);
      loadData();
    } catch (err) {
      alert('❌ Erreur import: ' + (err.response?.data?.error || err.message));
    }
    e.target.value = '';
  }

  async function handleToggle(jour) {
    try {
      await reportingFinancierApi.updateJourOuvre(jour.id, !jour.est_ouvre);
      setData(prev => prev.map(d => d.id === jour.id ? { ...d, est_ouvre: !d.est_ouvre } : d));
      // Recharger les counts
      const params = { annee };
      if (departement) params.departement = departement;
      const countsRes = await reportingFinancierApi.getJoursOuvresCount(params);
      setCounts(countsRes.data || { mensuel: [], totaux: [] });
    } catch (err) {
      alert('❌ Erreur mise à jour');
    }
  }

  // Regrouper les jours par département > mois
  const deptFilter = departement || (dimensions.departements.length > 0 ? dimensions.departements[0] : '');
  const filteredData = deptFilter ? data.filter(d => d.departement === deptFilter) : data;

  // Organiser par mois
  const moisData = {};
  for (const jour of filteredData) {
    const d = parseLocalDate(jour.date);
    const m = d.getMonth();
    if (!moisData[m]) moisData[m] = [];
    moisData[m].push(jour);
  }

  // Total ouvrés pour le département sélectionné
  const totalOuvres = (counts.totaux || []).find(t => !deptFilter || t.departement === deptFilter);

  return (
    <div className="rb-jo-tab">
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />

      <div className="page-toolbar">
        <select className="page-select" value={annee} onChange={e => setAnnee(parseInt(e.target.value))}>
          {(dimensions.annees.length > 0 ? dimensions.annees : [new Date().getFullYear()]).map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select className={`page-select ${departement ? 'active-filter' : ''}`} value={departement} onChange={e => setDepartement(e.target.value)}>
          <option value="">Tous départements</option>
          {dimensions.departements.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        {totalOuvres && (
          <span className="rb-jo-total">
            Total : <strong>{totalOuvres.total}</strong> jours ouvrés
          </span>
        )}
      </div>

      {loading ? (
        <div className="rb-loading">Chargement...</div>
      ) : filteredData.length === 0 ? (
        <div className="rb-empty">
          <p>Aucune donnée jours ouvrés pour {annee}.</p>
          <p>Importez un fichier Excel pour commencer.</p>
        </div>
      ) : (
        <div className="rb-jo-calendar">
          {Array.from({ length: 12 }, (_, mi) => {
            const jours = moisData[mi] || [];
            const countMois = (counts.mensuel || []).find(c =>
              c.mois === mi + 1 && (!deptFilter || c.departement === deptFilter)
            );
            // Calculer le premier jour de la semaine pour le padding
            const firstDay = jours.length > 0 ? parseLocalDate(jours[0].date).getDay() : 0;

            return (
              <div key={mi} className="rb-jo-month">
                <div className="rb-jo-month-header">
                  <span className="rb-jo-month-name">{MOIS_LABELS[mi]} {annee}</span>
                  <span className="rb-jo-month-count">{countMois?.jours_ouvres || 0}j</span>
                </div>
                <div className="rb-jo-weekdays">
                  {JOURS_SEMAINE.map(j => <span key={j} className="rb-jo-wd">{j[0]}</span>)}
                </div>
                <div className="rb-jo-days">
                  {/* Padding pour aligner le 1er jour */}
                  {Array.from({ length: firstDay }, (_, i) => (
                    <span key={`pad-${i}`} className="rb-jo-day rb-jo-pad" />
                  ))}
                  {jours.map(jour => {
                    const d = parseLocalDate(jour.date);
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <span
                        key={jour.id}
                        className={`rb-jo-day ${jour.est_ouvre ? 'rb-jo-ouvre' : 'rb-jo-ferme'} ${isWeekend ? 'rb-jo-weekend' : ''}`}
                        onClick={() => handleToggle(jour)}
                        title={`${d.getDate()}/${mi + 1} — ${jour.est_ouvre ? 'Ouvré' : 'Fermé'} (clic pour changer)`}
                      >
                        {d.getDate()}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Composant principal ────────────────────────────────────────────────────

const SUB_TABS = [
  { id: 'budget', icon: '💰', label: 'Budget' },
  { id: 'jours',  icon: '📅', label: 'Jours Ouvrés' },
  { id: 'synthese', icon: '📊', label: 'Synthèse' },
];

function ReportingBudget({ user, setHeaderAction }) {
  const [subTab, setSubTab] = useState('budget');

  return (
    <div className="rb-container">
      <div className="rb-sub-tabs">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            className={`rb-sub-tab ${subTab === t.id ? 'active' : ''}`}
            onClick={() => setSubTab(t.id)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {subTab === 'budget' && <BudgetTab user={user} setHeaderAction={setHeaderAction} />}
      {subTab === 'jours' && <JoursOuvresTab user={user} setHeaderAction={setHeaderAction} />}
      {subTab === 'synthese' && (
        <div className="rb-empty">
          <h3>📊 Synthèse Budget vs Réel</h3>
          <p>Section en cours de construction. Les comparatifs seront branchés prochainement.</p>
        </div>
      )}
    </div>
  );
}

export default ReportingBudget;
